import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { injectModalRef } from '../../../../shared/modal';
import { NotificationService, SettingsService } from '../../../../core/services';
import { apiErrorMessage } from '../../../../core/utils/api-error-message';
import { initials } from '../../../../core/utils/initials';

export interface AvatarModalData {
  name: string | null;
}

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

@Component({
  selector: 'app-avatar-modal',
  standalone: false,
  templateUrl: './avatar-modal.html',
  styleUrl: './avatar-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarModal {
  readonly data = input.required<AvatarModalData>();
  readonly modalRef = injectModalRef<boolean>();

  private readonly settingsService = inject(SettingsService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly initials = initials;
  protected readonly file = signal<File | null>(null);
  protected readonly previewUrl = signal<string | null>(null);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);

  protected onFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.error.set('Choose an image file (JPG, PNG or GIF).');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      this.error.set('That image is over 5 MB. Choose a smaller one.');
      return;
    }

    this.error.set(null);
    this.file.set(file);
    const reader = new FileReader();
    reader.onload = () => this.previewUrl.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  protected save(): void {
    const file = this.file();
    if (!file) {
      this.modalRef.dismiss();
      return;
    }

    this.saving.set(true);
    this.error.set(null);
    this.modalRef.setDisableClose(true);

    this.settingsService.uploadAvatar(file)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.saving.set(false);
          this.modalRef.setDisableClose(false);
        }),
      )
      .subscribe({
        next: () => {
          this.notificationService.success('Avatar updated successfully');
          this.modalRef.close(true);
        },
        error: error => this.error.set(apiErrorMessage(error, 'An error occurred while uploading your avatar. Please try again later.')),
      });
  }
}
